import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon, ExclamationIcon, TrashIcon } from '@heroicons/react/outline'
import { useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import {
    createShiftSchedule,
    updateShiftSchedule,
    deleteShiftSchedule,
} from '../../../services/workforceServices'
import { extractBackendError, invalidateWorkforce } from '../../../utils/workforceUtils'

export const SchedulePeriodFormDialog = ({ warehouse_id, shifts, period, defaults = {}, onClose }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const editing = !!period

    const [open, setOpen] = useState(false)
    const [values, setValues] = useState({
        work_shift_id: String(period?.work_shift_id ?? period?.work_shift?.id ?? defaults.work_shift_id ?? ''),
        start_date: period?.start_date || defaults.start_date || '',
        end_date: period?.end_date || defaults.end_date || '',
    })
    const [confirmingDelete, setConfirmingDelete] = useState(false)
    const [fieldErrors, setFieldErrors] = useState({})
    const [baseError, setBaseError] = useState('')

    useEffect(() => { setOpen(true) }, [])

    const close = (didChange = false) => {
        setOpen(false)
        setTimeout(() => onClose(didChange), 250)
    }

    const handleSuccess = () => {
        invalidateWorkforce(queryClient, warehouse_id, ['schedules', 'attendance'])
        close(true)
    }

    const applyError = (err) => {
        const { fields, base, message } = extractBackendError(err)
        setFieldErrors(fields)
        if (base.length) setBaseError(base.join(' · '))
        else setBaseError(message)
    }

    const createMutation = useMutation((data) => createShiftSchedule(warehouse_id, data), { onSuccess: handleSuccess, onError: applyError })
    const updateMutation = useMutation((data) => updateShiftSchedule(warehouse_id, period.id, data), { onSuccess: handleSuccess, onError: applyError })
    const deleteMutation = useMutation(() => deleteShiftSchedule(warehouse_id, period.id), { onSuccess: handleSuccess, onError: applyError })

    const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

    const validate = () => {
        const next = {}
        if (!editing && !values.work_shift_id) next.work_shift_id = t('workforce.schedule.errors.shift_required')
        if (!values.start_date) next.start_date = t('workforce.schedule.errors.start_required')
        if (!values.end_date) next.end_date = t('workforce.schedule.errors.end_required')
        if (values.start_date && values.end_date && values.end_date < values.start_date) {
            next.end_date = t('workforce.schedule.errors.end_before_start')
        }
        setFieldErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSubmit = (e) => {
        e?.preventDefault?.()
        setBaseError('')
        if (!validate()) return
        if (editing) {
            updateMutation.mutate({ start_date: values.start_date, end_date: values.end_date })
        } else {
            createMutation.mutate({
                work_shift_id: Number(values.work_shift_id),
                start_date: values.start_date,
                end_date: values.end_date,
            })
        }
    }

    const handleDelete = () => {
        if (!confirmingDelete) { setConfirmingDelete(true); return }
        deleteMutation.mutate()
    }

    const title = editing ? t('workforce.schedule.edit_title') : t('workforce.schedule.create_title')
    const selectedShift = shifts.find((s) => String(s.id) === String(values.work_shift_id))

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-20 overflow-y-auto" open={open} onClose={() => close(false)}>
                <div className="flex items-center justify-center min-h-screen px-4">
                    <Dialog.Overlay className="fixed inset-0 bg-gray-700 bg-opacity-40" />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <Dialog.Title className="text-lg font-medium">{title}</Dialog.Title>
                            <button type="button" onClick={() => close(false)} className="text-gray-400 hover:text-gray-500">
                                <XIcon className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                            {baseError && <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">{baseError}</div>}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.schedule.shift')}</label>
                                <select
                                    disabled={editing}
                                    value={values.work_shift_id}
                                    onChange={(e) => setValues({ ...values, work_shift_id: e.target.value })}
                                    className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2 bg-white disabled:bg-gray-100"
                                >
                                    <option value="">{t('workforce.schedule.select_shift')}</option>
                                    {shifts.map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name} ({(s.start_time || '').slice(0, 5)}–{(s.end_time || '').slice(0, 5)})
                                        </option>
                                    ))}
                                </select>
                                {fieldErrors.work_shift_id && <p className="mt-1 text-xs text-red-600">{fieldErrors.work_shift_id}</p>}
                                {selectedShift?.active === false && (
                                    <p className="mt-1 text-xs text-amber-700">{t('workforce.schedule.shift_inactive_hint')}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.schedule.start_date')}</label>
                                    <input
                                        type="date"
                                        value={values.start_date}
                                        onChange={(e) => setValues({ ...values, start_date: e.target.value })}
                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                    />
                                    {fieldErrors.start_date && <p className="mt-1 text-xs text-red-600">{fieldErrors.start_date}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('workforce.schedule.end_date')}</label>
                                    <input
                                        type="date"
                                        value={values.end_date}
                                        onChange={(e) => setValues({ ...values, end_date: e.target.value })}
                                        className="block w-full border border-gray-300 rounded-md text-sm px-3 py-2"
                                    />
                                    {fieldErrors.end_date && <p className="mt-1 text-xs text-red-600">{fieldErrors.end_date}</p>}
                                </div>
                            </div>

                            <p className="text-xs text-gray-500">{t('workforce.schedule.range_hint')}</p>

                            {editing && confirmingDelete && (
                                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2">
                                    <ExclamationIcon className="h-5 w-5 flex-shrink-0" />
                                    <div>{t('workforce.schedule.delete_warning')}</div>
                                </div>
                            )}
                        </form>

                        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between gap-2">
                            <div>
                                {editing && (
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        disabled={isSubmitting}
                                        className={`inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium border disabled:opacity-50 ${confirmingDelete ? 'bg-red-600 text-white border-red-600 hover:bg-red-700' : 'text-red-700 border-red-200 bg-white hover:bg-red-50'}`}
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                        {confirmingDelete ? t('workforce.schedule.confirm_delete') : t('workforce.schedule.delete')}
                                    </button>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button type="button" onClick={() => close(false)} disabled={isSubmitting}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50">
                                    {t('workforce.cancel')}
                                </button>
                                <button type="button" onClick={handleSubmit} disabled={isSubmitting}
                                    className="px-4 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                                    {isSubmitting ? t('workforce.saving') : t('workforce.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
